import { useState, useEffect, useRef, useMemo } from "react";
import type { Event } from "@/types";
import { IconButton } from "@components/atoms";
import { Text } from "@components/atoms";
import { EventCardSecondary } from "@components/molecules/EventCardSecondary";
import { Search, X } from "lucide-react";

interface SearchPanelProps {
  events: Event[];
  isOpen: boolean;
  onClose: () => void;
}

/** Read recent searches from localStorage (runs once at module load). */
function loadRecentSearches(): string[] {
  try {
    const saved = localStorage.getItem("ticketin_recent_searches");
    return saved ? (JSON.parse(saved) as string[]) : [];
  } catch {
    return [];
  }
}

/**
 * Search panel molecule.
 * Contains search bar + dropdown with trending events and recent searches.
 */
export function SearchPanel({ events, isOpen, onClose }: SearchPanelProps) {
  const [query, setQuery] = useState("");
  const [recentSearches, setRecentSearches] =
    useState<string[]>(loadRecentSearches);
  const inputRef = useRef<HTMLInputElement>(null);

  // Focus input when panel opens; reset query when closed via prop
  const prevOpen = useRef(isOpen);
  useEffect(() => {
    if (isOpen && !prevOpen.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
    prevOpen.current = isOpen;
  }, [isOpen]);

  // When panel closes externally, clear query
  const handleClose = () => {
    setQuery("");
    onClose();
  };

  const saveSearch = (term: string) => {
    const trimmed = term.trim();
    if (!trimmed) return;
    const updated = [
      trimmed,
      ...recentSearches.filter((s) => s !== trimmed),
    ].slice(0, 10);
    setRecentSearches(updated);
    localStorage.setItem("ticketin_recent_searches", JSON.stringify(updated));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && query.trim()) {
      saveSearch(query);
    }
  };

  // Filter events based on query
  const filteredEvents = useMemo(() => {
    if (query.trim()) {
      return events
        .filter(
          (ev) =>
            ev.name.toLowerCase().includes(query.toLowerCase()) ||
            ev.category.toLowerCase().includes(query.toLowerCase()) ||
            ev.location.city.toLowerCase().includes(query.toLowerCase()),
        )
        .slice(0, 5);
    }
    return events.slice(0, 5);
  }, [events, query]);

  // Split recent searches into two columns
  const midpoint = Math.ceil(recentSearches.length / 2);
  const leftColumn = recentSearches.slice(0, midpoint);
  const rightColumn = recentSearches.slice(midpoint);

  if (!isOpen) return null;

  return (
    <div className="fixed top-0 left-0 right-0 z-50 flex flex-col items-end px-4 sm:px-8 lg:px-12 py-4 sm:py-6">
      {/* Search bar row */}
      <div className="flex w-full items-center gap-3">
        <div
          className="flex flex-1 items-center rounded-lg bg-white px-4 min-w-0"
          style={{ height: 40 }}
        >
          <Search size={20} className="flex-shrink-0 text-gray-400" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="¿Qué evento estás buscando?"
            className="ml-3 flex-1 border-none bg-transparent text-body text-primary-110 outline-none placeholder:text-gray-400"
          />
        </div>
        <IconButton size={24} onClick={handleClose} className="text-white">
          <X size={24} />
        </IconButton>
      </div>

      {/* Dropdown panel */}
      <div
        className="mt-3 w-full overflow-hidden overflow-y-auto rounded-lg bg-white p-4 sm:p-6 shadow-xl"
        style={{ maxHeight: 530 }}
      >
        {/* Trending / Filtered events */}
        <Text
          variant="h3"
          as="h3"
          className="mb-4 text-primary-110 font-semibold"
          style={{ fontSize: 17 }}
        >
          {query.trim() ? "Resultados" : "Eventos en tendencia"}
        </Text>

        <div className="flex gap-[18px] overflow-x-auto pb-4">
          {filteredEvents.map((event) => (
            <div key={event.id} className="flex-shrink-0 w-40">
              <EventCardSecondary event={event} />
            </div>
          ))}
          {filteredEvents.length === 0 && (
            <Text variant="body" className="text-primary-100/60 py-8">
              No se encontraron eventos para "{query}"
            </Text>
          )}
        </div>

        {/* Recent searches — only shown when no query */}
        {!query.trim() && recentSearches.length > 0 && (
          <div className="mt-6">
            <Text
              variant="h3"
              as="h3"
              className="mb-4 text-primary-110 font-semibold"
              style={{ fontSize: 17 }}
            >
              Búsquedas recientes
            </Text>
            <div className="flex gap-8">
              {/* Left column */}
              <div className="flex-1">
                {leftColumn.map((search, idx) => (
                  <div key={`l-${idx}`}>
                    <button
                      className="w-full cursor-pointer border-none bg-transparent py-2 text-left text-body text-primary-110 hover:text-primary"
                      style={{ fontSize: 14 }}
                      onClick={() => setQuery(search)}
                    >
                      {search}
                    </button>
                    {idx < leftColumn.length - 1 && (
                      <div className="h-px bg-gray-200" />
                    )}
                  </div>
                ))}
              </div>
              {/* Right column */}
              <div className="flex-1">
                {rightColumn.map((search, idx) => (
                  <div key={`r-${idx}`}>
                    <button
                      className="w-full cursor-pointer border-none bg-transparent py-2 text-left text-body text-primary-110 hover:text-primary"
                      style={{ fontSize: 14 }}
                      onClick={() => setQuery(search)}
                    >
                      {search}
                    </button>
                    {idx < rightColumn.length - 1 && (
                      <div className="h-px bg-gray-200" />
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
